import * as React from 'react'

import { X } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import Localization from '../../localization/LocalizationManager'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import { BULLET } from '../../utils/usefulStrings'
import { Button } from '../Button/Button'

interface IProps {
  titleCaption: string
  titleIconEl: JSX.Element
  onCloseClick: () => void

  startDate?: number | Date
  endDate?: number | Date
  isCreationMode?: boolean
  menuEl?: JSX.Element
  renderStatus?: (className: string) => JSX.Element

  projectDateStore?: ProjectDateStore
}

const newRequest = 'New request'

// Passed to consumers for the status slot; DeliveryCompactHeaderBar composes
// its own tag sizing on top of it.
const STATUS_CLASS = 'tw:inline-block tw:truncate tw:text-app-micro'

@inject('projectDateStore')
@observer
export default class CompactHeaderBar extends React.Component<IProps> {
  public render() {
    const { titleCaption, titleIconEl, onCloseClick } = this.props

    return (
      <div className="tw:flex tw:h-12 tw:grow-0 tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-border tw:bg-sidebar tw:px-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={Localization.translator.close}
          className="tw:-ml-1.5 tw:text-muted-foreground"
          onClick={onCloseClick}
          data-testid="form-close-button"
        >
          <X className="tw:size-5" />
        </Button>
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:justify-center tw:overflow-hidden">
          <div className="tw:flex tw:items-center">
            <div className="tw:ml-2.5 tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:[&_svg:not([class*=size-])]:size-5">
              {titleIconEl}
            </div>
            <div
              className="tw:ml-1.5 tw:min-w-0 tw:flex-1 tw:truncate tw:text-label-lg-prominent tw:text-foreground"
              title={titleCaption}
            >
              {titleCaption}
            </div>
          </div>
          {this.renderDateAndStatusRow()}
        </div>
        {!this.props.isCreationMode && this.props.menuEl}
        {this.renderNewRequestStatus()}
      </div>
    )
  }

  private renderDateAndStatusRow(): JSX.Element {
    const { isCreationMode, renderStatus, startDate, endDate } = this.props

    if (isCreationMode || (!startDate && !endDate)) {
      return null
    }

    return (
      <div className="tw:flex tw:items-center">
        <div
          className="tw:ml-2.5 tw:truncate tw:text-body-md tw:text-muted-foreground"
          title={this.dateRangeCaption}
        >
          {this.dateRangeCaption}
        </div>
        <div className="tw:ml-1.5 tw:flex tw:items-center tw:text-label-sm tw:text-muted-foreground">
          {BULLET}
        </div>
        <div className="tw:flex tw:items-center tw:truncate">
          {renderStatus?.(STATUS_CLASS)}
        </div>
      </div>
    )
  }

  private renderNewRequestStatus(): JSX.Element {
    if (!this.props.isCreationMode) {
      return null
    }

    return (
      <div className="tw:ml-2.5 tw:flex tw:h-6 tw:flex-none tw:items-center tw:rounded-full tw:border tw:border-solid tw:border-border tw:bg-background tw:px-2.5 tw:text-app-micro tw:text-foreground">
        {newRequest}
      </div>
    )
  }

  private get dateRangeCaption(): string {
    const { startDate, endDate, projectDateStore } = this.props

    const {
      getWeekdayMonthAndDayToDisplay,
      getMonthAndDayToDisplay,
      getShortTimeIntervalPerDay,
      isSameDay,
    } = projectDateStore

    const isTheSameDay = isSameDay(startDate, endDate)

    if (isTheSameDay) {
      return `${getMonthAndDayToDisplay(
        startDate,
      )}, ${getShortTimeIntervalPerDay(startDate, endDate)}`
    }

    return `${getWeekdayMonthAndDayToDisplay(
      startDate,
    )} - ${getWeekdayMonthAndDayToDisplay(endDate)}`
  }
}
